import type { LabObservationView } from '../../types/labResults';
import { calculateLabFlagScore, LAB_FLAG_SCORE } from './calculateLabFlagScore';
import styles from './PatientHealthOverview.module.css';

export function LabFlagScore({ observations }: { observations: LabObservationView[] }) {
  const score = calculateLabFlagScore(observations);
  return <section className={styles.score} aria-label="Lab flag score">
    <div className={styles.scoreSummary}>
      <div className={styles.scoreRing}>
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="50" r="43" fill="none" stroke="#e5e7eb" strokeWidth="6" />
          {score.value !== null && <circle cx="50" cy="50" r="43" pathLength="100" fill="none" stroke="currentColor" strokeWidth="6" strokeDasharray={`${score.value} 100`} transform="rotate(-90 50 50)" />}
        </svg>
        <div><strong>{score.value ?? '—'}</strong>{score.value !== null && <span>/ 100</span>}</div>
      </div>
      <div>
        <h3>Lab flag score</h3>
        <p>Lab-based estimate · {LAB_FLAG_SCORE.version}</p>
        <p>{score.value === null ? 'No markers have a recognized lab flag. Score unavailable.' : `${score.assessed} of ${score.total} markers have a supplied flag.`}</p>
        {!!score.unassessed && <p>{score.unassessed} without a recognized flag; excluded from scoring.</p>}
      </div>
    </div>
    {!!score.critical && <p className={styles.criticalScore} role="status">{score.critical} {score.critical === 1 ? 'marker is' : 'markers are'} flagged critical by the lab. Review the flagged results regardless of this score.</p>}
    <p className={styles.scoreCaveat}>This experimental score summarizes lab flags. It is not a validated measure of overall health.</p>
    <details className={styles.scoreMethod}>
      <summary>How this score is calculated</summary>
      <p>Start at {LAB_FLAG_SCORE.baseline}. Subtract {LAB_FLAG_SCORE.criticalPenalty} for each critical flag and {LAB_FLAG_SCORE.abnormalPenalty} for each other abnormal flag. The minimum is 0. Critical flags are counted once.</p>
      <p>This report: {score.critical} critical × {LAB_FLAG_SCORE.criticalPenalty}, plus {score.abnormal} other abnormal × {LAB_FLAG_SCORE.abnormalPenalty}.{score.value !== null && ` Result: ${score.value}/100.`}</p>
      <p>Normal flags have no deduction. Missing flags are excluded, so an incomplete report can still score 100. Scores depend on which tests were ordered and should not be compared across different panels. Body selection and search do not change the report’s score.</p>
    </details>
  </section>;
}
